import { watch } from 'vue'
import { createI18n } from 'vue-i18n'
import moment from 'moment'
import en from '@/locales/en.json'
import fi from '@/locales/fi.json'
import sv from '@/locales/sv.json'
import 'moment/dist/locale/fi'
import 'moment/dist/locale/sv'

const messages = {
  en,
  fi,
  sv,
}

type Locale = keyof typeof messages

const SUPPORTED_LOCALES = Object.keys(messages) as Locale[]

/**
 * The visitor's own language choice: the first language in the browser's preference list that the site supports
 * ("en-US, sv-SE" -> en, "de-DE, sv" -> sv), or null when none is supported. Used instead of guessing from the
 * visitor's IP address, which is wrong for travellers, VPNs and expats and would mean processing personal data.
 * Tracking pages for recipients in Sweden and Finland use Swedish and Finnish regardless (TrackingNumberInfo).
 */
export const browserLocale = (): Locale | null => {
  if (typeof navigator === 'undefined') {
    return null
  }

  const languages = navigator.languages?.length ? navigator.languages : [navigator.language || '']
  for (const language of languages) {
    const normalizedLanguage = language.toLowerCase().split('-')[0]
    const supported = SUPPORTED_LOCALES.find((locale) => locale === normalizedLanguage)
    if (supported) {
      return supported
    }
  }

  return null
}

export const i18n = createI18n({
  legacy: false,
  locale: browserLocale() || 'en',
  fallbackLocale: 'en',
  messages,
})

// Keep <html lang> in step so screen readers and translation tools use the right language
if (typeof document !== 'undefined') {
  watch(
    i18n.global.locale,
    (locale) => {
      document.documentElement.lang = locale
      moment.locale(locale) // dates follow the page language
    },
    { immediate: true },
  )
}
